// Iter 82.9 Sprint 2 / Phase E3.1 — Payroll Run Console.
//
// Master detail orchestrator for a single payroll run. Tabs:
//   • Overview     — totals strip + status badge + action bar
//   • Lines        — per-employee payroll line table
//   • Exceptions   — grouped by severity with override + filter
//   • Preview      — operator-mandated approval-readiness dashboard
//   • Manual Inputs — variable bonus / reimb / adjustment / recovery
//   • Timeline     — audit/compliance vertical view (read-only)
//
// State-aware action bar mirrors the backend state machine:
//   Draft       → Calculate · Discard
//   Calculated  → Recalculate · Submit · Revert · Discard
//   Pending     → Approve · Reject
//   Approved    → Lock
//   Locked      → Disburse · Correction
//   Disbursed   → Correction
//   Discarded   → (terminal)
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Calculator, PaperPlaneRight, CheckCircle, XCircle, Lock,
    HandCoins, ArrowsClockwise, Trash, GitBranch, Snowflake,
    ArrowLeft, Eye, MagnifyingGlass, Warning,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { toast } from "sonner";
import PayrollRunTimeline from "../../components/payroll/PayrollRunTimeline";
import PayrollExceptionsPanel from "./_payroll/PayrollExceptionsPanel";
import PayrollLinesPanel from "./_payroll/PayrollLinesPanel";
import PayrollPreviewPanel from "./_payroll/PayrollPreviewPanel";
import PayrollManualInputsPanel from "./_payroll/PayrollManualInputsPanel";
import PayrollPayslipsPanel from "./_payroll/PayrollPayslipsPanel";
import PayrollBankExportPanel from "./_payroll/PayrollBankExportPanel";

const STATUS_TONE = {
    Draft: "bg-slate-100 text-slate-700",
    Calculated: "bg-blue-100 text-blue-800",
    "Pending Approval": "bg-amber-100 text-amber-800",
    Approved: "bg-indigo-100 text-indigo-800",
    Locked: "bg-violet-100 text-violet-800",
    Disbursed: "bg-emerald-100 text-emerald-800",
    Discarded: "bg-zinc-100 text-zinc-600",
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function Tile({ label, value, tone, testId }) {
    return (
        <div className={`px-4 py-3 rounded-lg border bg-white ${tone || ""}`} data-testid={testId}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-lg font-mono font-semibold mt-0.5">{value}</div>
        </div>
    );
}

export default function PayrollRunConsole() {
    const { rid } = useParams();
    const nav = useNavigate();
    const { can } = useAuth();
    const canAdmin = can("payroll:admin");
    const canApprove = can("payroll:approve");

    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [payloadDrawer, setPayloadDrawer] = useState(null);

    const reload = useCallback(async () => {
        try {
            const r = await api.get(`/payroll/run/${rid}`);
            setRun(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load run"));
        } finally {
            setLoading(false);
        }
    }, [rid]);

    useEffect(() => { reload(); }, [reload]);

    const act = async (action, body) => {
        try {
            await api.post(`/payroll/run/${rid}/${action}`, body || {});
            toast.success(`Action "${action}" succeeded`);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, `Action ${action} failed`));
        }
    };

    if (loading) return <div className="p-6 text-slate-500" data-testid="console-loading">Loading…</div>;
    if (!run) return <div className="p-6 text-rose-700" data-testid="console-error">Run not found.</div>;

    const status = run.status;
    const totals = run.totals || {};
    const variance = run.variance_to_prior_month || {};
    const excCount = run.exception_count_by_severity || {};

    return (
        <div className="space-y-4" data-testid="payroll-run-console">
            <div className="flex items-center gap-2 text-sm text-slate-500">
                <Link to="/admin/payroll/runs" className="hover:underline inline-flex items-center gap-1" data-testid="console-back">
                    <ArrowLeft size={14} /> Payroll Runs
                </Link>
            </div>
            <PageHeader
                eyebrow={`Payroll Run · ${run.id}`}
                title={`Run for ${run.period_month}`}
                description={`${run.period_start} → ${run.period_end} · ${run.cohort_size || 0} employees · calc v${run.calc_version || 0}`}
                actions={(
                    <div className="flex items-center gap-2 flex-wrap" data-testid="console-action-bar">
                        <Badge className={`${STATUS_TONE[status] || "bg-slate-100"} text-sm`} data-testid="console-status">
                            {status}
                        </Badge>
                        {run.correction_of_run_id && (
                            <Badge variant="outline" className="text-[10px]">Correction of {run.correction_of_run_id}</Badge>
                        )}
                        {/* State-aware action buttons */}
                        {status === "Draft" && canAdmin && (
                            <>
                                <Button size="sm" onClick={() => act("calculate")} data-testid="console-calculate-btn">
                                    <Calculator size={14} className="mr-1" /> Calculate
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => act("discard")} data-testid="console-discard-btn">
                                    <Trash size={14} className="mr-1" /> Discard
                                </Button>
                            </>
                        )}
                        {status === "Calculated" && canAdmin && (
                            <>
                                <Button size="sm" onClick={() => act("calculate")} variant="outline" data-testid="console-recalculate-btn">
                                    <ArrowsClockwise size={14} className="mr-1" /> Recalculate
                                </Button>
                                <Button size="sm" onClick={() => act("submit")} data-testid="console-submit-btn">
                                    <PaperPlaneRight size={14} className="mr-1" /> Submit for Approval
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => act("revert-to-draft")} data-testid="console-revert-btn">
                                    Revert to Draft
                                </Button>
                            </>
                        )}
                        {status === "Pending Approval" && canApprove && (
                            <>
                                <Button size="sm" onClick={() => act("approve")} data-testid="console-approve-btn">
                                    <CheckCircle size={14} className="mr-1" /> Approve (4-eye)
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)} data-testid="console-reject-btn">
                                    <XCircle size={14} className="mr-1" /> Reject
                                </Button>
                            </>
                        )}
                        {status === "Approved" && canApprove && (
                            <Button size="sm" onClick={() => act("lock")} data-testid="console-lock-btn">
                                <Lock size={14} className="mr-1" /> Lock Period
                            </Button>
                        )}
                        {status === "Locked" && canAdmin && (
                            <Button size="sm" onClick={() => act("disburse")} data-testid="console-disburse-btn">
                                <HandCoins size={14} className="mr-1" /> Mark Disbursed
                            </Button>
                        )}
                        {(status === "Locked" || status === "Disbursed") && canAdmin && (
                            <Button variant="outline" size="sm" onClick={() => nav(`/admin/payroll/runs?correction=${rid}`)} data-testid="console-correction-btn">
                                <GitBranch size={14} className="mr-1" /> Correction Run
                            </Button>
                        )}
                    </div>
                )}
            />

            {/* Totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="console-totals">
                <Tile testId="tile-headcount" label="Headcount" value={totals.headcount || 0} />
                <Tile testId="tile-gross" label="Gross Payroll" value={inr(totals.gross_earnings)} />
                <Tile testId="tile-deductions" label="Total Deductions" value={inr(totals.gross_deductions)} />
                <Tile testId="tile-net" label="Net Payable" value={inr(totals.net_pay)} />
                <Tile testId="tile-employer" label="Employer Contributions" value={inr(totals.employer_contributions)} />
                <Tile testId="tile-ctc" label="CTC for Period" value={inr(totals.ctc_for_period)} />
            </div>

            {/* Variance + Exception summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="border rounded-lg bg-white p-4" data-testid="console-variance">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Variance vs Prior Locked Run</div>
                    {variance.pct === null || variance.pct === undefined ? (
                        <div className="text-sm text-slate-400 mt-1">No prior locked run to compare.</div>
                    ) : (
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className={`text-2xl font-mono font-semibold ${Math.abs(variance.pct) > (run.variance_thresholds_pct?.total_payroll_mom || 5) ? "text-rose-700" : "text-emerald-700"}`}>
                                {variance.pct > 0 ? "+" : ""}{Number(variance.pct).toFixed(2)}%
                            </span>
                            <span className="text-sm text-slate-500">({variance.delta > 0 ? "+" : ""}{inr(variance.delta)})</span>
                            <span className="ml-2 text-xs text-slate-400">vs {variance.prior_run_id}</span>
                        </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">
                        Threshold: ±{run.variance_thresholds_pct?.total_payroll_mom || 5}% total payroll
                    </div>
                </div>
                <div className="border rounded-lg bg-white p-4" data-testid="console-exception-summary">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Open Exceptions</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {["Critical", "High", "Medium", "Low", "Info"].map((s) => {
                            const n = excCount[s] || 0;
                            const tone = s === "Critical" ? "bg-rose-100 text-rose-800"
                                : s === "High" ? "bg-amber-100 text-amber-800"
                                : s === "Medium" ? "bg-yellow-50 text-yellow-700"
                                : s === "Low" ? "bg-slate-100 text-slate-600"
                                : "bg-sky-50 text-sky-700";
                            return (
                                <Badge key={s} className={`${tone} font-semibold`} data-testid={`exc-pill-${s}`}>
                                    {s}: {n}
                                </Badge>
                            );
                        })}
                    </div>
                </div>
            </div>

            <Tabs defaultValue="lines" className="w-full" data-testid="console-tabs">
                <TabsList className="grid w-full grid-cols-7 max-w-5xl">
                    <TabsTrigger value="lines" data-testid="tab-lines">Lines</TabsTrigger>
                    <TabsTrigger value="exceptions" data-testid="tab-exceptions">Exceptions</TabsTrigger>
                    <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
                    <TabsTrigger value="manual" data-testid="tab-manual">Manual Inputs</TabsTrigger>
                    <TabsTrigger value="payslips" data-testid="tab-payslips">Payslips</TabsTrigger>
                    <TabsTrigger value="bank-export" data-testid="tab-bank-export">Bank Export</TabsTrigger>
                    <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                </TabsList>
                <TabsContent value="lines">
                    <PayrollLinesPanel runId={rid} calcVersion={run?.calc_version} runStatus={run?.status} onRunChanged={reload} />
                </TabsContent>
                <TabsContent value="exceptions">
                    <PayrollExceptionsPanel runId={rid} canEdit={canAdmin} onResolved={reload} calcVersion={run?.calc_version} />
                </TabsContent>
                <TabsContent value="preview">
                    <PayrollPreviewPanel runId={rid} run={run} />
                </TabsContent>
                <TabsContent value="manual">
                    <PayrollManualInputsPanel runId={rid} run={run} canEdit={canAdmin} onSaved={reload} />
                </TabsContent>
                <TabsContent value="payslips">
                    <PayrollPayslipsPanel runId={rid} run={run} canEdit={canAdmin} onRefresh={reload} />
                </TabsContent>
                <TabsContent value="bank-export">
                    <PayrollBankExportPanel runId={rid} run={run} canEdit={canAdmin} onRefresh={reload} />
                </TabsContent>
                <TabsContent value="timeline">
                    <PayrollRunTimeline runId={rid} onSelectPayload={setPayloadDrawer} />
                </TabsContent>
            </Tabs>

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent data-testid="reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Reject this run</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Run will return to Draft. The reason is
                            permanently logged on the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Label className="text-xs">Reason (≥5 chars, mandatory)</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="e.g. Variance exceeds policy; need to fix Mr X's LWD"
                            data-testid="reject-reason"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="reject-cancel">Cancel</Button>
                        <Button onClick={async () => {
                            await act("reject", { reason: rejectReason });
                            setRejectOpen(false);
                            setRejectReason("");
                        }} data-testid="reject-confirm">Reject Run</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Sheet open={!!payloadDrawer} onOpenChange={(v) => !v && setPayloadDrawer(null)}>
                <SheetContent className="max-w-lg w-full" data-testid="payload-drawer">
                    <SheetHeader>
                        <SheetTitle>Audit Payload — {payloadDrawer?.action}</SheetTitle>
                    </SheetHeader>
                    <div className="text-xs text-slate-500 mt-2">
                        {payloadDrawer?.actor_name || payloadDrawer?.actor_email} · {payloadDrawer?.created_at}
                    </div>
                    <pre className="text-xs bg-slate-50 border rounded p-3 mt-3 overflow-auto font-mono" data-testid="payload-json">
                        {payloadDrawer ? JSON.stringify(payloadDrawer.payload, null, 2) : ""}
                    </pre>
                </SheetContent>
            </Sheet>
        </div>
    );
}
